import React, { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { formatMoney } from '../../api/bookingApi';
import { useBooking } from '../../booking/BookingContext';
import { buildSeatGrid } from '../../booking/seatGrid';

const COLORS = {
  available: '#2E7D32',
  taken: '#E53935',
  selected: '#F9A825',
};

function SeatButton({ seat, selected, onToggle }) {
  if (!seat) {
    return <div className="w-10 h-11" />;
  }
  const occupied = seat.occupied;
  const color = occupied
    ? COLORS.taken
    : selected
      ? COLORS.selected
      : COLORS.available;

  return (
    <button
      type="button"
      disabled={occupied}
      onClick={() => onToggle(seat.label)}
      className="w-10 h-11 rounded-t-2xl rounded-b-md text-white text-xs font-extrabold disabled:cursor-not-allowed shadow-sm"
      style={{ backgroundColor: color }}
      aria-label={seat.label}
    >
      {seat.label}
    </button>
  );
}

export default function SeatsPage() {
  const booking = useBooking();
  const navigate = useNavigate();
  const trip = booking.selectedTrip;

  const grid = useMemo(
    () => buildSeatGrid(trip?.seats || []),
    [trip?.seats]
  );

  if (!trip) return <Navigate to="/book/trips" replace />;

  const remaining = booking.passengers - booking.selectedSeats.length;
  const ready = remaining === 0;

  return (
    <div className="space-y-4 pb-28">
      <div className="bg-white rounded-2xl border border-pink-100 p-4 text-center">
        <p className="font-semibold text-sm text-gray-800">
          {trip.originName} → {trip.destinationName} · {trip.departureTime}
        </p>
        <div className="mt-3 flex justify-center gap-4 text-xs text-gray-600">
          <span className="inline-flex items-center gap-1.5">
            <span
              className="w-3.5 h-3.5 rounded"
              style={{ background: COLORS.available }}
            />
            Available
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              className="w-3.5 h-3.5 rounded"
              style={{ background: COLORS.taken }}
            />
            Taken
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              className="w-3.5 h-3.5 rounded"
              style={{ background: COLORS.selected }}
            />
            Selected
          </span>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-pink-100 p-4 overflow-x-auto">
        {grid.rows.length === 0 ? (
          <p className="text-center text-gray-500 py-8 text-sm">
            Seat map unavailable for this trip.
          </p>
        ) : (
          <div className="inline-flex flex-col items-center gap-2.5 min-w-full">
            {grid.rows.map((row, ri) => {
              const isLast = ri === grid.rows.length - 1;
              const flat = [
                ...row.left.filter(Boolean),
                ...(row.aisle ? [row.aisle] : []),
                ...row.right.filter(Boolean),
              ].sort((a, b) => a.columnIndex - b.columnIndex);

              if (isLast && flat.length >= 4) {
                return (
                  <div key={ri} className="flex gap-1.5 justify-center w-full">
                    {flat.map((seat) => (
                      <SeatButton
                        key={seat.label}
                        seat={seat}
                        selected={booking.selectedSeats.includes(seat.label)}
                        onToggle={booking.toggleSeat}
                      />
                    ))}
                  </div>
                );
              }

              return (
                <div key={ri} className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    {row.left.map((seat, i) => (
                      <SeatButton
                        key={seat?.label || `l-${i}`}
                        seat={seat}
                        selected={
                          seat
                            ? booking.selectedSeats.includes(seat.label)
                            : false
                        }
                        onToggle={booking.toggleSeat}
                      />
                    ))}
                  </div>
                  <div className="w-8 flex justify-center">
                    {row.aisle && (
                      <SeatButton
                        seat={row.aisle}
                        selected={booking.selectedSeats.includes(
                          row.aisle.label
                        )}
                        onToggle={booking.toggleSeat}
                      />
                    )}
                  </div>
                  <div className="flex gap-1.5">
                    {row.right.map((seat, i) => (
                      <SeatButton
                        key={seat?.label || `r-${i}`}
                        seat={seat}
                        selected={
                          seat
                            ? booking.selectedSeats.includes(seat.label)
                            : false
                        }
                        onToggle={booking.toggleSeat}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {booking.selectedSeats.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {booking.selectedSeats.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => booking.toggleSeat(s)}
              className="bg-white border border-gray-200 rounded-full px-3 py-1 text-sm font-semibold"
            >
              {s} ×
            </button>
          ))}
        </div>
      )}

      <div className="fixed inset-x-0 bottom-0 z-40 pointer-events-none">
        <div className="max-w-3xl mx-auto px-4 pb-4 pt-2 pointer-events-auto">
          <div className="bg-white rounded-2xl border border-pink-100 shadow-lg p-4 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500">
                {ready
                  ? `${booking.selectedSeats.length} seat(s) selected`
                  : `Select ${remaining} more seat(s)`}
              </p>
              <p className="font-heading font-bold text-lg text-gray-900">
                {formatMoney(booking.total, trip.currency || 'TZS')}
              </p>
            </div>
            <button
              type="button"
              disabled={!ready}
              onClick={() => navigate('/book/details')}
              className="bg-superfeo-pink disabled:bg-gray-300 text-white font-bold px-5 py-3 rounded-xl"
            >
              {ready ? 'Continue' : 'Select seats'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
